const COLLECTION_TABS_PRODUCTS_SECTION_ID = 'collection-tabs-carousel-products';
const COLLECTION_TABS_PRODUCT_SECTION_ID = 'collection-tabs-carousel-product';

// ---------------------------------------------------------------------------
// Constructor identity + behavioral tracking, mirrored from
// assets/product-recommendations-constructor.js (the PDP YMAL pod carousel) so
// pod views/clicks on this carousel attribute to the same visitor + session as
// SDK-fired events elsewhere on the site.
// ---------------------------------------------------------------------------

const CONSTRUCTOR_SERVICE_URL = 'https://ac.cnstrc.com';
const CONSTRUCTOR_CLIENT_VERSION = 'cio-theme-hp-carousel-1.0.0';
const ANON_UI_KEY = 'constructor_anonymous_ui';
const CIO_CLIENT_ID_COOKIE = 'ConstructorioID_client_id';
const CIO_SESSION_ID_LOCAL = '_constructorio_search_session_id';
const CIO_SESSION_DATA_COOKIE = 'ConstructorioID_session';

function readCookie(name) {
  const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const match = document.cookie.match(new RegExp('(?:^|;\\s*)' + escaped + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : null;
}

function generateUuidV4() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function getConstructorClientId() {
  try {
    let id = readCookie(CIO_CLIENT_ID_COOKIE);
    if (!id) {
      id = generateUuidV4();
      const expires = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toUTCString();
      document.cookie = `${CIO_CLIENT_ID_COOKIE}=${id}; expires=${expires}; path=/`;
    }
    return id;
  } catch {
    return generateUuidV4();
  }
}

function getConstructorSessionId() {
  try {
    const parsed = parseInt(localStorage.getItem(CIO_SESSION_ID_LOCAL), 10);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
  } catch {
    /* localStorage blocked — fall through */
  }
  try {
    const raw = readCookie(CIO_SESSION_DATA_COOKIE);
    if (raw) {
      const sid = parseInt(JSON.parse(raw)?.sessionId, 10);
      if (Number.isFinite(sid) && sid > 0) return sid;
    }
  } catch {
    /* malformed cookie — fall through */
  }
  return 1;
}

function getCustomerId() {
  try {
    const id = window.ShopifyAnalytics?.meta?.page?.customerId;
    return id ? String(id) : null;
  } catch {
    return null;
  }
}

function getOrCreateAnonId() {
  try {
    let id = localStorage.getItem(ANON_UI_KEY);
    if (!id) {
      id =
        (typeof crypto !== 'undefined' && crypto.randomUUID && crypto.randomUUID()) ||
        `anon-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem(ANON_UI_KEY, id);
    }
    return id;
  } catch {
    return `anon-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }
}

function getUserIdentifier() {
  return getCustomerId() || getOrCreateAnonId();
}

function constructorIdentityParams(apiKey) {
  const params = {
    key: apiKey,
    i: getConstructorClientId(),
    s: getConstructorSessionId(),
    ui: getUserIdentifier(),
    c: CONSTRUCTOR_CLIENT_VERSION,
    _dt: Date.now(),
  };
  try {
    const { host, pathname } = window.location;
    if (host) params.origin_referrer = host + (pathname || '');
  } catch {
    /* no-op */
  }
  return params;
}

function sendConstructorBeacon(action, apiKey, eventFields, { beacon = false } = {}) {
  if (!apiKey) return;
  try {
    const identity = constructorIdentityParams(apiKey);
    const url = new URL(`${CONSTRUCTOR_SERVICE_URL}/v2/behavioral_action/${action}`);
    Object.entries(identity).forEach(([key, value]) => {
      url.searchParams.set(key, String(value));
    });
    const body = JSON.stringify({ ...eventFields, ...identity });

    if (beacon && typeof navigator?.sendBeacon === 'function') {
      const blob = new Blob([body], { type: 'text/plain' });
      if (navigator.sendBeacon(url.toString(), blob)) return;
    }
    fetch(url.toString(), {
      method: 'POST',
      mode: 'cors',
      keepalive: true,
      headers: { 'Content-Type': 'text/plain' },
      body,
    }).catch(() => {
      /* tracking must never break the UI */
    });
  } catch (err) {
    console.warn('Collection tabs carousel: tracking beacon failed', err);
  }
}

function trackRecommendationView({ apiKey, podId, resultId, resultCount, items }) {
  sendConstructorBeacon('recommendation_result_view', apiKey, {
    pod_id: podId,
    num_results_viewed: items.length,
    result_count: resultCount,
    result_page: 1,
    result_id: resultId,
    section: 'Products',
    url: window.location.href,
    items: items.map((item) => ({ item_id: item.itemId, item_name: item.itemName })),
  });
}

function trackRecommendationClick({ apiKey, podId, resultId, resultCount, numResultsPerPage, item }) {
  const fields = {
    pod_id: podId,
    strategy_id: podId,
    item_id: item.itemId,
    item_name: item.itemName,
    result_id: resultId,
    result_count: resultCount,
    result_page: 1,
    result_position_on_page: item.position,
    num_results_per_page: numResultsPerPage,
    section: 'Products',
  };
  if (item.variationId) fields.variation_id = item.variationId;
  sendConstructorBeacon('recommendation_result_click', apiKey, fields, { beacon: true });
}

function extractProductHandle(url) {
  if (!url) return null;
  // Constructor's `url` field is sometimes a bare product handle (the prod
  // feed does this), not a /products/ URL — mirror the React PLP's fallback.
  if (/^[a-z0-9][a-z0-9._-]*$/i.test(url)) return url;
  try {
    const path = new URL(url, window.location.origin).pathname;
    const match = path.match(/\/products\/([^/?#]+)/);
    return match ? match[1] : null;
  } catch {
    return null;
  }
}

async function fetchConstructorPod({ apiKey, podId, numResults }) {
  const url = new URL(`${CONSTRUCTOR_SERVICE_URL}/recommendations/v1/pods/${encodeURIComponent(podId)}`);
  url.searchParams.set('key', apiKey);
  url.searchParams.set('ui', getUserIdentifier());
  url.searchParams.set('i', getConstructorClientId());
  url.searchParams.set('s', String(getConstructorSessionId()));
  if (numResults) url.searchParams.set('num_results', String(numResults));
  const res = await fetch(url.toString());
  if (!res.ok) throw new Error(`Constructor pod HTTP ${res.status}`);
  const json = await res.json();
  const results = json?.response?.results || [];
  const items = results
    .map((result, index) => {
      const data = result?.data || {};
      return {
        handle: extractProductHandle(data.url),
        itemId: data.id != null ? String(data.id) : '',
        itemName: result?.value || '',
        variationId: data.variation_id != null ? String(data.variation_id) : '',
        position: index + 1,
        raw: result,
      };
    })
    .filter((item) => item.handle);
  const rawById = new Map();
  for (const result of results) {
    const data = result?.data || {};
    const id = data.id != null ? String(data.id) : data.product_id != null ? String(data.product_id) : '';
    if (id) rawById.set(id, result);
  }
  return {
    items,
    rawById,
    resultId: json?.result_id || '',
    resultCount: json?.response?.total_num_results ?? items.length,
  };
}

// The swatch-row builder lives in the recommendations module so pod-tab cards
// render the exact same swatch set as the PDP/cart recommendation cards.
// Loaded lazily: the homepage pays nothing until a pod tab actually fills.
let swatchModulePromise = null;
function loadSwatchModule() {
  if (!swatchModulePromise) {
    // Import via the VERSIONED asset url (from the section's script tag): a bare
    // relative import resolves to the unversioned /assets/ path, which Shopify's
    // CDN caches long-term — after a deploy this kept executing a stale module
    // (swatch tiles built by the previous release's markup). Falls back to the
    // relative path when the attr is absent.
    const versionedSrc = document.querySelector('script[data-recos-module-src]')?.dataset.recosModuleSrc;
    swatchModulePromise = import(versionedSrc || './product-recommendations-constructor.js').catch((err) => {
      swatchModulePromise = null;
      throw err;
    });
  }
  return swatchModulePromise;
}

// Parent-card fields buildSwatchRowFromResult needs for the active tile,
// read from the Liquid-rendered card so hover-out restores exactly what the
// card shipped with (image, price markup incl. any discount line, subtitle).
function cardSwatchParent(node) {
  const imgs = node.querySelectorAll('slideshow-slide img');
  const main = imgs[0]?.currentSrc || imgs[0]?.src || '';
  const hover = imgs[1]?.currentSrc || imgs[1]?.src || '';
  const link = node.querySelector('a[href*="/products/"]');
  return {
    title: node.querySelector('.collection-card-title__text')?.textContent?.trim() || '',
    subtitle: node.querySelector('.custom-product-subtitle')?.textContent?.trim() || '',
    priceHtml: node.querySelector('product-price')?.innerHTML?.trim() || '',
    url: link ? link.getAttribute('href') : '',
    imageUrl: main,
    hoverImageUrl: hover || main,
  };
}

// Replace each pod card's Liquid swatch row (Shopify colorway enumeration)
// with the row built from Constructor's swatches data — same builder, same
// count and same per-color prices as the PDP/cart recommendation cards. A
// result with no renderable Constructor swatches clears the row: the card
// shows what Constructor knows, exactly like those surfaces. Runs on the
// detached card nodes before fillPanelItems inserts them, so nothing flickers.
async function applyConstructorSwatches(mod, sectionEl, nodes, items, rawById, apiKey) {
  const targets = [];
  nodes.forEach((node, i) => {
    const raw = items[i]?.raw;
    if (!raw) return;
    let container = node.querySelector('.product-recommendations__swatches');
    // collection-tabs-carousel-product cards don't ship a swatch row. Inject
    // one so bundle (and regular) colourways can switch like the PDP recs.
    if (!container) {
      const host =
        node.querySelector('product-price')?.parentElement ||
        node.querySelector('.group-block-content');
      if (host) {
        container = document.createElement('div');
        container.className = 'product-recommendations__swatches';
        container.setAttribute('role', 'group');
        container.setAttribute('aria-label', 'Color');
        host.appendChild(container);
      }
    }
    targets.push({ node, container, result: raw });
  });
  if (!targets.length) return;
  const missing = mod.collectMissingSwatchIds(targets, rawById);
  if (missing.length) {
    const fetched = await mod.fetchResultsByIds(missing, apiKey);
    fetched.forEach((value, key) => rawById.set(key, value));
  }
  const discount = mod.readPromoScope(sectionEl?.dataset);
  for (const { node, container, result } of targets) {
    const groupIds = result?.data?.group_ids || result?.group_ids || [];
    // Offer scope: the page's "Discount Code Price" rules narrowed to this
    // product's group_ids. buildSwatchRowFromResult re-narrows it per sibling
    // colourway, and the card's own price comes from the Liquid render.
    const cardDiscount = mod.promoScopeFor(discount, groupIds);
    // Bundle cards: the Liquid render carries the wrapper's admin price and
    // featured image. Overlay Constructor's per-colour price, images and colour
    // link first, so the swatch row's active tile restores them on hover-out.
    // Runs even when this card has no swatch row — otherwise homepage pods
    // keep the wrapper's admin price.
    if (typeof mod.applyBundleCardOverrides === 'function') {
      mod.applyBundleCardOverrides(node, result, cardDiscount);
    }
    if (!container) continue;
    const row = mod.buildSwatchRowFromResult(result, cardSwatchParent(node), rawById, cardDiscount);
    container.innerHTML = row || '';
  }
}

// Section-render card fetches, shared across every carousel on the page.
// Pages like /pages/exclusive-specials stack 16+ of these sections, and the
// naive version fired one uncached fetch per product per section (100+
// simultaneous section-render requests) — the cache dedupes handles that
// appear in several pods, and the semaphore keeps the burst small enough
// that Shopify's section rendering doesn't throttle us.
const CARD_FETCH_CONCURRENCY = 6;
const productCardItemCache = new Map();
let activeCardFetches = 0;
const pendingCardFetches = [];

function withCardFetchSlot(task) {
  const run = () =>
    Promise.resolve()
      .then(task)
      .finally(() => {
        activeCardFetches -= 1;
        const next = pendingCardFetches.shift();
        if (next) {
          activeCardFetches += 1;
          next();
        }
      });
  if (activeCardFetches < CARD_FETCH_CONCURRENCY) {
    activeCardFetches += 1;
    return run();
  }
  return new Promise((resolve, reject) => {
    pendingCardFetches.push(() => run().then(resolve, reject));
  });
}

function fetchProductCardItem(handle) {
  const cached = productCardItemCache.get(handle);
  if (cached) return cached;
  const promise = withCardFetchSlot(async () => {
    const url = new URL(`/products/${encodeURIComponent(handle)}`, window.location.origin);
    url.searchParams.set('section_id', COLLECTION_TABS_PRODUCT_SECTION_ID);
    const res = await fetch(url.toString());
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const html = await res.text();
    const tpl = document.createElement('template');
    tpl.innerHTML = html;
    // Callers importNode(..., true) before mutating, so the cached template
    // node itself is never touched after this point.
    return tpl.content.querySelector('[data-collection-tabs-product] > .resource-list__item');
  }).catch((err) => {
    productCardItemCache.delete(handle);
    throw err;
  });
  productCardItemCache.set(handle, promise);
  return promise;
}

// ---------------------------------------------------------------------------
// Single-template pod rendering. One themed card render per pod panel (cached
// by handle, shared across sections) serves as the template for every result;
// each clone is repopulated from Constructor's data — same pattern as the PDP
// YMAL carousel in product-recommendations-constructor.js. Replaces the
// previous card-per-product section renders (up to 8 per pod), which kept the
// carousel skeleton on screen well past page load.
// ---------------------------------------------------------------------------

const cardTemplatePromises = new Map();
function fetchCardTemplate(handle) {
  if (!cardTemplatePromises.has(handle)) {
    const promise = fetchProductCardItem(handle).then((node) => {
      if (!node) throw new Error('empty card template');
      // Work on a private copy: productCardItemCache shares `node` with the
      // fallback/collection paths, which need the full untrimmed card.
      const template = document.importNode(node, true);
      // Constructor supplies exactly a main + hover image: trim extra slides,
      // and when the template product had only ONE image, duplicate it so
      // every clone still has a hover slot to populate.
      const slides = template.querySelectorAll('slideshow-slide[ref="slides[]"]');
      slides.forEach((slide, i) => {
        if (i >= 2) slide.remove();
      });
      if (slides.length === 1) {
        const hoverSlide = document.importNode(slides[0], true);
        ['id', 'slide-id', 'data-slide-id'].forEach((attr) => {
          const value = hoverSlide.getAttribute(attr);
          if (value) hoverSlide.setAttribute(attr, `${value}-hover`);
        });
        slides[0].after(hoverSlide);
      }
      const segments = template.querySelectorAll('[data-fit-pagination-segment]');
      segments.forEach((seg, i) => {
        if (i >= 2) seg.remove();
      });
      if (segments.length === 1) {
        const seg = document.importNode(segments[0], true);
        seg.classList.remove('is-active');
        seg.setAttribute('data-slide-index', '1');
        seg.setAttribute('aria-pressed', 'false');
        seg.setAttribute('aria-label', 'Go to image 2');
        segments[0].after(seg);
      }
      return template;
    });
    promise.catch(() => cardTemplatePromises.delete(handle));
    cardTemplatePromises.set(handle, promise);
  }
  return cardTemplatePromises.get(handle);
}

// Feed data that can't populate a faithful clone renders through its own
// Liquid section render instead: no usable price (dev bundles), a swatch
// thumbnail or nothing as image_url, or — the prod suit-bundle shape — a
// priced result with a real photo but neither a hover image nor fit_options.
// Cloning those from an apparel template would hand them quick-add and a
// hover slot their own Liquid card never renders.
function needsLiquidCard(data) {
  const price = Number(data?.current_price ?? data?.price);
  const image = data?.image_url || '';
  if (!(Number.isFinite(price) && price > 0)) return true;
  if (!image || /(?:^|\/)swatch_/i.test(image)) return true;
  const hasHover = Boolean(data?.hover_image_url);
  const hasFits = Array.isArray(data?.fit_options) && data.fit_options.length > 0;
  return !hasHover && !hasFits;
}

// A template should come from a fully-featured result — priced, with real
// main + hover images — so its cloned markup (hover slide, fit footer,
// quick-shop) isn't crippled for the whole pod by one sparse product.
function isTemplateCandidate(data) {
  return !needsLiquidCard(data) && Boolean(data?.hover_image_url);
}

function withImageWidth(url, width) {
  if (!url) return '';
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}width=${width}`;
}

function setTabsSlideImage(slide, src, alt) {
  if (!slide || !src) return;
  const img = slide.querySelector('img');
  if (!img) return;
  // Explicit src only: dropping srcset/sizes sidesteps the `sizes="auto, …"` +
  // width="3840" resolution trap on AJAX-inserted lazy images.
  img.setAttribute('src', withImageWidth(src, 800));
  img.removeAttribute('srcset');
  img.removeAttribute('sizes');
  img.setAttribute('alt', alt || '');
}

// Repopulate a cloned template card (.resource-list__item) so it reads as the
// Constructor result's product. Keeps the theme's exact markup; swaps only the
// data. `mod` is the loaded product-recommendations-constructor module.
function populateTabsCardFromResult(cardItem, result, mod, discount) {
  const data = result?.data || {};
  // Combined-listing results use a composite id ("9779602227441-mediterranean-
  // pine"); DOM/Elevar/PowerReviews expect the numeric Shopify product id.
  const rawId = String(data.id ?? data.product_id ?? '');
  const id = (rawId.match(/^\d+/) || [rawId])[0];
  const title = result?.value || data.title || '';
  const handle = extractProductHandle(data.url) || '';
  const url = data.url ? mod.relativeProductUrl(data.url) : handle ? `/products/${handle}` : '';
  const main = data.image_url || '';
  const hover = data.hover_image_url || '';

  // Ids embed the template product's id (block ids, quick-add form id) — swap
  // it for this product's id so clones don't collide.
  const templateId = cardItem.querySelector('[data-product-id]')?.getAttribute('data-product-id') || '';
  if (templateId && id && templateId !== id) {
    cardItem.querySelectorAll('[id], [data-block-id], [form], [for]').forEach((el) => {
      ['id', 'data-block-id', 'form', 'for'].forEach((attr) => {
        const value = el.getAttribute(attr);
        if (value && value.includes(templateId)) {
          el.setAttribute(attr, value.split(templateId).join(id));
        }
      });
    });
  }
  if (id) {
    cardItem.querySelectorAll('[data-product-id]').forEach((el) => {
      el.setAttribute('data-product-id', id);
    });
  }
  // The card-link anchor id is an md5 of the template card — suffix it so
  // every clone keeps a unique id.
  const cardLink = cardItem.querySelector('a[ref="productCardLink"]');
  if (cardLink?.id && id) cardLink.id = `${cardLink.id}-${id}`;

  // Links + accessible labels.
  if (url) {
    cardItem
      .querySelectorAll('a[ref="productCardLink"], a[ref="cardGalleryLink"], a[ref="productTitleLink"]')
      .forEach((a) => a.setAttribute('href', url));
  }
  const hiddenTitle = cardItem.querySelector('a[ref="productCardLink"] .visually-hidden');
  if (hiddenTitle) hiddenTitle.textContent = title;
  const galleryLink = cardItem.querySelector('a[ref="cardGalleryLink"]');
  if (galleryLink && title) galleryLink.setAttribute('aria-label', title);
  const transitionWrapper = cardItem.querySelector('product-card-link');
  if (transitionWrapper && main) {
    transitionWrapper.setAttribute('data-featured-media-url', withImageWidth(main, 800));
  }

  // Title, zoom-out title, subtitle (color parsed from the active swatch).
  const titleLeaf = cardItem.querySelector('.collection-card-title__text');
  if (titleLeaf) titleLeaf.textContent = title;
  const zoomOutTitle = cardItem.querySelector('.product-grid-view-zoom-out--details .h4');
  if (zoomOutTitle) zoomOutTitle.textContent = title;
  const subtitleWrapper = cardItem.querySelector('.custom-product-subtitle-wrapper');
  const color =
    mod.activeSwatchColor(result) || data.color || data.Color || colorFromHandle(handle, title);
  if (subtitleWrapper) {
    subtitleWrapper.innerHTML = color
      ? `<span class="custom-product-subtitle">${escapeText(color)}</span>`
      : '';
  }

  // Price — same offer scope as the YMAL cards. A result with no usable price
  // (e.g. bundles: Constructor sends none) clears the price rather than
  // leaking the template product's.
  const groupIds = data.group_ids || result?.group_ids || [];
  const scopeDiscount = mod.promoScopeFor(discount, groupIds);
  const priceHtml = mod.buildPriceHtml(data, scopeDiscount);
  const priceContainer =
    cardItem.querySelector("product-price [ref='priceContainer']") ||
    cardItem.querySelector('product-price');
  if (priceContainer) priceContainer.innerHTML = priceHtml || '';

  // Badges: only the sale badge is derivable from Constructor data.
  const badgesContainer = cardItem.querySelector('.product-badges');
  if (badgesContainer) {
    badgesContainer.innerHTML = mod.isOnSale(data)
      ? '<div class="product-badges__badge product-badges__badge--rectangle color-scheme-1">Selling Fast</div>'
      : '';
  }

  // Gallery: main + hover slide.
  const slides = cardItem.querySelectorAll('slideshow-slide[ref="slides[]"]');
  setTabsSlideImage(slides[0], main, title);
  setTabsSlideImage(slides[1], hover || main, title);

  // Fit footer: rebuild the "N FITS" label from the feed's fit_options (the
  // template's count is its own product's); keep quick-shop trigger + pagination.
  const fitFooter = cardItem.querySelector('[data-fit-footer]');
  if (fitFooter) {
    const fitCount = Array.isArray(data.fit_options) ? data.fit_options.length : 0;
    let fitsLabel = fitFooter.querySelector('span.product-card-media-footer__fits');
    if (fitCount > 1) {
      if (!fitsLabel) {
        const fitsWrap = fitFooter.querySelector('div.product-card-media-footer__fits');
        if (fitsWrap) {
          fitsLabel = document.createElement('span');
          fitsLabel.className = 'product-card-media-footer__fits';
          fitsWrap.prepend(fitsLabel);
        }
      }
      if (fitsLabel) fitsLabel.textContent = `${fitCount} FITS`;
    } else if (fitsLabel) {
      fitsLabel.remove();
    }
  }
  cardItem.querySelectorAll('[data-collection-tabs-quick-add-url]').forEach((el) => {
    el.setAttribute('data-collection-tabs-quick-add-url', url);
    el.setAttribute('data-collection-tabs-quick-add-title', title);
    el.setAttribute('aria-label', `Open quick shop for ${title}`);
  });

  // Quick add: the dialog resolves its product from the (rewritten) card link,
  // but force the "choose" button so the inline one-variant add form — still
  // the template product's — can never fire.
  const quickAdd = cardItem.querySelector('[ref="quickAdd"]');
  if (quickAdd) {
    quickAdd.setAttribute('data-product-title', title);
    quickAdd.setAttribute('data-quick-add-button', 'choose');
    const variantInput = quickAdd.querySelector('[ref="variantId"]');
    if (variantInput) variantInput.value = data.variation_id != null ? String(data.variation_id) : '';
  }

  // PowerReviews: re-key the star snippet and let the global CategorySnippet
  // observer re-render it after insert. The feed's program_id is the same page
  // id snippets/powerreviews-page-id.liquid resolves; a product with no rating
  // under the fallback id renders nothing and the star self-hides.
  const reviews = cardItem.querySelector('.power-reviews-star-rating-simple');
  if (reviews) {
    const prPageId =
      (data.program_id ? String(data.program_id).replace(/-final-sale/gi, '') : '') || id;
    if (id) reviews.setAttribute('data-id', id);
    reviews.classList.remove('pr-rating-checked', 'hidden');
    const prComponent = reviews.querySelector('[data-pr-component]');
    if (prComponent) {
      prComponent.innerHTML = '';
      if (prPageId) prComponent.setAttribute('data-pr-page-id', prPageId);
    }
  }

  // Elevar payload for dl_view_item_list / dl_select_item (dollar prices, like
  // the Liquid original; enrichment fills sku/variant from Shopify JSON).
  const elevarScript = cardItem.querySelector('script.elevar-tc-item');
  if (elevarScript) {
    elevarScript.textContent = JSON.stringify({
      id,
      product_id: id,
      variant_id: data.variation_id != null ? String(data.variation_id) : '',
      name: title,
      brand: data.brand || data.vendor || '',
      category: data.product_type || data.productType || data.type || '',
      variant: color,
      price: toNumberOrZero(data.current_price ?? data.price),
      compare_at_price: toNumberOrZero(data.full_price ?? data.compare_at_price),
      image: main,
      url,
    });
  }
}

function escapeText(value) {
  const div = document.createElement('div');
  div.textContent = value == null ? '' : String(value);
  return div.innerHTML;
}

// Colourway handles are "<handleized title>-<colour words>[-N]" in this
// catalog (mirrors the Liquid card's colour-from-handle derivation). Used when
// the result carries no swatch image to parse the colour name from.
function colorFromHandle(handle, title) {
  if (!handle || !title) return '';
  const words = String(title)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .split('-')
    .filter(Boolean);
  // Titles sometimes carry a prefix the handle omits ("Italian Stealth Blazer"
  // vs stealth-blazer-bright-navy-blue-0): drop leading words until the handle
  // starts with the rest, keeping at least two words so the match stays specific.
  for (let start = 0; start <= words.length - 2; start += 1) {
    const prefix = words.slice(start).join('-');
    if (!handle.startsWith(`${prefix}-`)) continue;
    const rest = handle.slice(prefix.length + 1).replace(/-\d+$/, '');
    if (!rest || rest.length > 40) return '';
    return rest
      .split('-')
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }
  return '';
}

// Liquid's elevar-tc-item emits 0 for missing prices — keep that shape.
function toNumberOrZero(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function attachPodClickTracking(panel, { apiKey, podId, resultId, resultCount, numResultsPerPage, items }) {
  if (panel._podClickTrackingAttached) return;
  panel._podClickTrackingAttached = true;
  const byHandle = new Map(items.map((item) => [item.handle, item]));
  panel.addEventListener(
    'click',
    (event) => {
      const link = event.target?.closest?.('a[href*="/products/"]');
      if (!link || !panel.contains(link)) return;
      const item = byHandle.get(extractProductHandle(link.href));
      if (!item) return;
      trackRecommendationClick({
        apiKey,
        podId,
        resultId,
        resultCount,
        numResultsPerPage,
        item,
      });
    },
    { capture: true }
  );
}

/**
 * @param {string} collectionPageUrl
 */
function buildCollectionTabsProductsFetchUrl(collectionPageUrl) {
  const url = new URL(collectionPageUrl, window.location.origin);
  url.searchParams.set('section_id', COLLECTION_TABS_PRODUCTS_SECTION_ID);
  return url.toString();
}

/**
 * @param {string} html
 */
function parseCollectionTabsProductsMarkup(html) {
  const tpl = document.createElement('template');
  tpl.innerHTML = html;
  let root = tpl.content.querySelector('[data-collection-tabs-products]');
  if (!root) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    root = doc.querySelector('[data-collection-tabs-products]');
  }
  if (!root) {
    const wrap = document.createElement('div');
    wrap.innerHTML = html;
    root = wrap.querySelector('[data-collection-tabs-products]');
  }
  return root;
}

class CollectionTabsCarousel {
  constructor(section) {
    this.section = section;
    this.tabs = Array.from(section.querySelectorAll('[role="tab"]'));
    this.tabsContainer = section.querySelector('.collection-tabs-carousel__tabs');
    this.panels = Array.from(section.querySelectorAll('[role="tabpanel"]'));
    this.sectionId = section.dataset.sectionId || '';
    this.storageKey = this.sectionId ? `collection-tabs-carousel:${this.sectionId}` : '';
    this.panelContent = new Map();
    this.pagination = section.querySelector('[data-carousel-pagination]');
    this.navPrev = section.querySelector('[data-carousel-prev]');
    this.navNext = section.querySelector('[data-carousel-next]');
    this.activePanel = null;
    this.activeSlideshow = null;
    this.activeScroller = null;
    this.activeSlides = [];
    this.onSlideshowSelect = null;
    this.onScrollerScroll = null;
    this.scrollRafId = null;
    this.resizeRafId = null;
    this.imageListeners = [];
    this.scrollingTimeout = null;
    this.paginationMeta = { totalPages: 0, slidesPerView: 0 };
    this.scrollerResizeObserver = null;
    this.lockedPage = null;
    this.lockedUntil = 0;
    this.lockedTargetIndex = null;
    this.productCardCarouselEnabled = section.dataset.productCardCarousel === 'true';
    // Until the section approaches the viewport, panel product loads are
    // deferred — on carousel-heavy pages every section used to fetch its pod
    // and all of its product cards at page load, which is what crashed the
    // 16-carousel bundles page. Flipped to false by deferInitialLoad().
    this.deferredInitialLoad = true;

    this.onTabClick = this.onTabClick.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onResize = this.onResize.bind(this);
    this.onPrevClick = this.onPrevClick.bind(this);
    this.onNextClick = this.onNextClick.bind(this);
    this.onCardGalleryClick = this.onCardGalleryClick.bind(this);

    this.tabs.forEach((tab) => tab.addEventListener('click', this.onTabClick));
    this.section.addEventListener('keydown', this.onKeyDown);
    this.section.addEventListener('click', this.onCardGalleryClick, true);
    window.addEventListener('resize', this.onResize);
    if (this.navPrev) this.navPrev.addEventListener('click', this.onPrevClick);
    if (this.navNext) this.navNext.addEventListener('click', this.onNextClick);

    const savedIndex = this.getSavedIndex();
    if (savedIndex !== null) {
      this.activateTab(savedIndex);
    }

    this.setupLazyPanels();
    this.enableCardGallerySlideshows();
    this.setupPagination();
    this.initElevarTracking();

    const activePanelIndex = this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
    this.deferInitialLoad();

    if (this.tabsContainer && activePanelIndex === 0) {
      window.requestAnimationFrame(() => {
        try {
          this.tabsContainer.scrollTo({ left: 0, behavior: 'auto' });
        } catch (e) {
          this.tabsContainer.scrollLeft = 0;
        }
      });
    }
  }

  // Load the active panel's products only once the section is within ~600px
  // of the viewport. Panels a visitor never scrolls to cost nothing.
  deferInitialLoad() {
    const start = () => {
      this.deferredInitialLoad = false;
      const activeIndex = this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
      const panel = activeIndex >= 0 ? this.panels[activeIndex] : this.panels[0];
      if (!panel) return;
      const hasSource = panel.dataset.productsUrl || panel.dataset.podId;
      if (hasSource && panel.dataset.productsLoaded === 'false') {
        this.loadPanelProducts(panel).then(() => this.observePanelCards(panel));
      } else if (!panel.hasAttribute('hidden')) {
        this.observePanelCards(panel);
      }
    };

    if (!('IntersectionObserver' in window)) {
      start();
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        start();
      },
      { rootMargin: '600px 0px' }
    );
    observer.observe(this.section);
  }

  onTabClick(event) {
    const tab = event.currentTarget;
    const index = Number(tab.dataset.tabIndex || 0);
    this.activateTab(index);
  }

  onKeyDown(event) {
    if (!this.tabs.length) return;
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;

    const currentIndex = this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
    const direction = event.key === 'ArrowRight' ? 1 : -1;
    const nextIndex = (currentIndex + direction + this.tabs.length) % this.tabs.length;
    this.activateTab(nextIndex);
    this.tabs[nextIndex]?.focus();
  }

  onCardGalleryClick(event) {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const quickAddButton = target.closest('.quick-add__button--choose');
    if (quickAddButton instanceof HTMLElement && this.section.contains(quickAddButton)) {
      return;
    }

    const quickAddTrigger = target.closest('[data-collection-tabs-quick-add-url]');
    if (quickAddTrigger instanceof HTMLElement && this.section.contains(quickAddTrigger)) {
      this.openQuickAddFromCarouselTrigger(event, quickAddTrigger);
      return;
    }

    const galleryLink = target.closest('a[ref="cardGalleryLink"]');
    if (!(galleryLink instanceof HTMLAnchorElement) || !this.section.contains(galleryLink)) return;

    if (target.closest('button, slideshow-arrows, slideshow-controls, .slideshow-controls')) {
      event.preventDefault();
      return;
    }
  }

  openQuickAddFromCarouselTrigger(event, trigger) {
    if (event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1)) return;

    const productUrl = trigger.dataset.collectionTabsQuickAddUrl || '';
    if (!productUrl) return;

    // Quick-shop open is a list click — fire dl_select_item here because the
    // stopImmediatePropagation() below prevents the delegated onElevarSelectItem
    // listener from ever seeing this click.
    this.pushElevarSelectItem(trigger.closest('.resource-list__item'));

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    const card = trigger.closest('product-card, .product-card');
    if (card?.dataset.productCardAction !== 'quick_add') {
      window.location.href = productUrl;
      return;
    }

    if (!document.getElementById('quick-add-dialog')) {
      window.location.href = productUrl;
      return;
    }

    this.dispatchQuickAddOpen(trigger, productUrl, trigger.dataset.collectionTabsQuickAddTitle || '');
  }

  dispatchQuickAddOpen(sourceElement, productUrl, title) {
    const card = sourceElement.closest('product-card, .product-card');
    const mediaRoot = card?.querySelector('a[ref="cardGalleryLink"]') || card || sourceElement;
    const image = mediaRoot.querySelector?.('img');
    const selectedColor =
      card?.querySelector('.custom-product-subtitle')?.textContent?.trim() ||
      card?.querySelector('.collection-card-title')?.textContent?.trim() ||
      '';

    document.dispatchEvent(
      new CustomEvent('bonobos:open-quick-add', {
        bubbles: true,
        detail: {
          productUrl,
          selectedColor,
          initial: {
            title,
            image: image instanceof HTMLImageElement ? image.currentSrc || image.src || '' : '',
            color: selectedColor,
          },
        },
      })
    );
  }

  activateTab(index) {
    this.tabs.forEach((tab, tabIndex) => {
      const isActive = tabIndex === index;
      tab.setAttribute('aria-selected', String(isActive));
      if (isActive) {
        tab.setAttribute('tabindex', '0');
      } else {
        tab.setAttribute('tabindex', '-1');
      }
    });

    this.panels.forEach((panel, panelIndex) => {
      const isActive = panelIndex === index;
      if (isActive) {
        panel.removeAttribute('hidden');
        panel.removeAttribute('inert');
        this.mountPanel(panel);
        const finishActivate = () => {
          this.resetCarousel(panel);
          window.requestAnimationFrame(() => this.setupPanelPagination(panel));
          this.enableCardGallerySlideshows();
          this.observePanelCards(panel);
        };
        if (
          !this.deferredInitialLoad &&
          panel.dataset.productsLoaded === 'false' &&
          (panel.dataset.productsUrl || panel.dataset.podId)
        ) {
          this.loadPanelProducts(panel).then(finishActivate).catch(() => finishActivate());
        } else {
          finishActivate();
        }
      } else {
        panel.setAttribute('hidden', '');
        panel.setAttribute('inert', '');
      }
    });

    this.saveIndex(index);
  }

  getPanelCarouselRoot(panel) {
    let el = panel.querySelector('.collection-tabs-carousel__carousel');
    if (!el) {
      const frag = this.panelContent.get(panel);
      if (frag) {
        el = frag.querySelector('.collection-tabs-carousel__carousel');
      }
    }
    return el;
  }

  fillPanelItems(panel, imported) {
    const carouselRoot = this.getPanelCarouselRoot(panel);
    if (!carouselRoot) return false;

    const skeletons = carouselRoot.querySelectorAll('.resource-list__item--skeleton');
    imported.forEach((node, i) => {
      const sk = skeletons[i];
      if (!sk) return;
      const slide = sk.closest('slideshow-slide');
      if (slide) {
        slide.innerHTML = '';
        slide.appendChild(node);
      }
    });
    carouselRoot.querySelectorAll('.resource-list__item--skeleton').forEach((sk) => {
      const slide = sk.closest('slideshow-slide');
      slide?.remove();
    });
    panel.dataset.productsLoaded = 'true';
    if (!panel.hasAttribute('hidden')) {
      this.resetCarousel(panel);
      window.requestAnimationFrame(() => this.setupPanelPagination(panel));
      this.enableCardGallerySlideshows();
      this.observePanelCards(panel);
    }
    return true;
  }

  getPanelSeoProductHandles(panel) {
    let seoList = panel.querySelector('.collection-tabs-carousel__seo-links');
    if (!seoList) {
      const frag = this.panelContent.get(panel);
      if (frag) seoList = frag.querySelector('.collection-tabs-carousel__seo-links');
    }
    if (!seoList) return [];
    return Array.from(seoList.querySelectorAll('a[href*="/products/"]'))
      .map((link) => extractProductHandle(link.getAttribute('href')))
      .filter(Boolean);
  }

  async loadPanelCollectionProducts(panel) {
    const limit = Number(panel.dataset.productLimit || 8);
    const handles = this.getPanelSeoProductHandles(panel).slice(0, limit);
    if (handles.length) {
      const cards = await Promise.all(handles.map((handle) => fetchProductCardItem(handle).catch(() => null)));
      const imported = cards.filter(Boolean).map((node) => document.importNode(node, true));
      if (imported.length) return this.fillPanelItems(panel, imported);
    }
    const url = panel.dataset.productsUrl;
    if (!url) return false;
    const fetchUrl = buildCollectionTabsProductsFetchUrl(url);
    const res = await fetch(fetchUrl);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const html = await res.text();
    const root = parseCollectionTabsProductsMarkup(html);
    if (!root) {
      console.warn('Collection tabs carousel: no [data-collection-tabs-products] in section response');
      return false;
    }

    const items = Array.from(root.querySelectorAll(':scope > .resource-list__item')).slice(0, limit);
    const imported = items.map((node) => document.importNode(node, true));
    return this.fillPanelItems(panel, imported);
  }

  async loadPanelPodProducts(panel) {
    const podId = panel.dataset.podId;
    const apiKey = panel.dataset.apiKey;
    if (!podId || !apiKey) return false;
    const limit = Number(panel.dataset.productLimit || 8);

    const pod = await fetchConstructorPod({ apiKey, podId, numResults: limit });
    if (!pod.items.length) {
      console.info(`Collection tabs carousel: pod "${podId}" returned no results`);
      return false;
    }

    let imported = [];
    let renderedItems = [];
    let mod = null;
    try {
      // Primary path: ONE rendered card as template, cloned + repopulated from
      // Constructor data for every result. Results the feed can't represent
      // faithfully (needsLiquidCard) get their own Liquid render instead.
      mod = await loadSwatchModule();
      // Only clone-friendly results need a template; prefer one with a real
      // hover image so the cloned markup is fully featured.
      const cloneItems = pod.items.filter((item) => !needsLiquidCard(item.raw?.data));
      let template = null;
      if (cloneItems.length) {
        const templateOrder = [
          ...cloneItems.filter((item) => isTemplateCandidate(item.raw?.data)),
          ...cloneItems.filter((item) => !isTemplateCandidate(item.raw?.data)),
        ];
        for (const item of templateOrder) {
          try {
            template = await fetchCardTemplate(item.handle);
            if (template) break;
          } catch {
            /* try the next handle */
          }
        }
        if (!template) throw new Error('no template card available');
      }
      const discount = mod.readPromoScope(this.section?.dataset);
      const rendered = await Promise.all(
        pod.items.map(async (item) => {
          if (needsLiquidCard(item.raw?.data)) {
            try {
              const node = await fetchProductCardItem(item.handle);
              return node ? { node: document.importNode(node, true), item } : null;
            } catch {
              return null;
            }
          }
          const node = document.importNode(template, true);
          try {
            populateTabsCardFromResult(node, item.raw, mod, discount);
            return { node, item };
          } catch (err) {
            console.warn(`Collection tabs carousel: failed to populate card for ${item.handle}`, err);
            return null;
          }
        })
      );
      rendered.filter(Boolean).forEach(({ node, item }) => {
        imported.push(node);
        renderedItems.push(item);
      });
      if (!imported.length) throw new Error('no cards populated from template');
    } catch (err) {
      console.warn(
        'Collection tabs carousel: template-card render failed, falling back to per-product fetches',
        err
      );
      const cards = await Promise.all(
        pod.items.map((item) => fetchProductCardItem(item.handle).catch(() => null))
      );
      imported = [];
      renderedItems = [];
      cards.forEach((node, i) => {
        if (!node) return;
        imported.push(document.importNode(node, true));
        renderedItems.push(pod.items[i]);
      });
    }
    if (!imported.length) return false;

    try {
      mod = mod || (await loadSwatchModule());
      await applyConstructorSwatches(mod, this.section, imported, renderedItems, pod.rawById, apiKey);
    } catch (err) {
      console.warn(
        'Collection tabs carousel: Constructor swatch render failed, keeping Liquid swatches',
        err
      );
    }

    const filled = this.fillPanelItems(panel, imported.slice(0, limit));
    if (filled) {
      trackRecommendationView({
        apiKey,
        podId,
        resultId: pod.resultId,
        resultCount: pod.resultCount,
        items: renderedItems,
      });
      attachPodClickTracking(panel, {
        apiKey,
        podId,
        resultId: pod.resultId,
        resultCount: pod.resultCount,
        numResultsPerPage: limit,
        items: renderedItems,
      });
    }
    return filled;
  }

  async loadPanelProducts(panel) {
    if (panel.dataset.productsLoaded === 'true') return;
    if (panel._tabsCarouselLoadPromise) return panel._tabsCarouselLoadPromise;

    if (!panel.dataset.podId && !panel.dataset.productsUrl) return;

    panel._tabsCarouselLoadPromise = (async () => {
      panel.dataset.productsLoading = 'true';
      try {
        if (panel.dataset.podId) {
          let podFilled = false;
          try {
            podFilled = await this.loadPanelPodProducts(panel);
          } catch (err) {
            console.warn('Collection tabs carousel: Constructor pod load failed', err);
          }
          let fallbackFailed = false;
          if (!podFilled && panel.dataset.productsUrl) {
            try {
              const fallbackFilled = await this.loadPanelCollectionProducts(panel);
              fallbackFailed = !fallbackFilled;
            } catch (err) {
              console.error('Collection tabs carousel: fallback collection load failed', err);
              fallbackFailed = true;
            }
          }
          // loadPanelCollectionProducts already marks the panel loaded on any
          // successful response (including an empty one), and returns false for
          // a thrown/invalid response. Only settle to an empty state here when
          // the fallback succeeded; a failed fallback must stay retryable
          // (productsLoaded='false') rather than be locked in as an empty load.
          if (!fallbackFailed && panel.dataset.productsLoaded !== 'true') {
            this.fillPanelItems(panel, []);
          }
        } else {
          await this.loadPanelCollectionProducts(panel);
        }
      } catch (err) {
        console.error('Collection tabs carousel: failed to load products', err);
      } finally {
        panel.dataset.productsLoading = 'false';
      }
    })();

    try {
      await panel._tabsCarouselLoadPromise;
    } finally {
      delete panel._tabsCarouselLoadPromise;
    }
  }

  setupLazyPanels() {
    const activeIndex = this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
    this.panels.forEach((panel, panelIndex) => {
      if (panelIndex === activeIndex) return;
      if (panel.children.length === 0) return;
      const fragment = document.createDocumentFragment();
      while (panel.firstChild) {
        fragment.appendChild(panel.firstChild);
      }
      this.panelContent.set(panel, fragment);
    });
  }

  enableCardGallerySlideshows() {
    if (!this.productCardCarouselEnabled) return;

    this.section
      .querySelectorAll('.collection-tabs-carousel-section a[ref="cardGalleryLink"] slideshow-component[disabled]')
      .forEach((slideshow) => {
        slideshow.removeAttribute('disabled');
        slideshow.setAttribute('disabled', 'false');
      });
  }

  mountPanel(panel) {
    if (panel.children.length > 0) return;
    const fragment = this.panelContent.get(panel);
    if (!fragment) return;
    panel.appendChild(fragment);
    this.panelContent.delete(panel);
  }

  resetCarousel(panel) {
    const slideshow = panel.querySelector('slideshow-component');
    if (slideshow && typeof slideshow.select === 'function') {
      slideshow.select(0, undefined, { animate: false });
      return;
    }
    const scroller = panel.querySelector('slideshow-slides');
    if (scroller) {
      scroller.scrollLeft = 0;
    }
  }

  setupPagination() {
    const activePanel = this.panels.find((panel) => !panel.hasAttribute('hidden'));
    if (activePanel) {
      this.setupPanelPagination(activePanel);
    }
  }

  setupPanelPagination(panel) {
    if (!this.pagination) return;

    const slideshow = panel.querySelector('slideshow-component');
    if (!slideshow) {
      this.pagination.hidden = true;
      this.setArrowState(this.navPrev, false);
      this.setArrowState(this.navNext, false);
      return;
    }

    this.cleanupActiveHandlers();

    this.activePanel = panel;
    this.activeSlideshow = slideshow;
    this.activeScroller = slideshow.querySelector('slideshow-slides');
    this.activeSlides = this.getSlides();

    this.onSlideshowSelect = () => this.syncControls();
    slideshow.addEventListener('slideshow:select', this.onSlideshowSelect);

    if (this.activeScroller) {
      this.scrollerResizeObserver = new ResizeObserver(() => this.syncControls());
      this.scrollerResizeObserver.observe(this.activeScroller);
      this.onScrollerScroll = () => {
        if (this.scrollRafId !== null) return;
        this.scrollRafId = window.requestAnimationFrame(() => {
          this.scrollRafId = null;
          this.syncControls();
        });
      };
      this.activeScroller.addEventListener('scroll', this.onScrollerScroll, { passive: true });
    }

    this.attachImageListeners(slideshow);
    this.syncControls();
    window.requestAnimationFrame(() => this.syncControls());
    setTimeout(() => this.syncControls(), 0);
  }

  syncControls() {
    if (!this.pagination || !this.activeSlideshow) return;

    const pageState = this.getPageState();
    if (!pageState) return;

    if (this.lockedPage !== null && performance.now() < this.lockedUntil) {
      if (this.isAtTargetIndex(this.lockedTargetIndex)) {
        this.clearLock();
        this.updatePagination(pageState);
        this.updateArrows(pageState);
        return;
      }

      this.updatePagination({
        ...pageState,
        currentPage: this.lockedPage,
        currentSlideIndex: this.lockedTargetIndex ?? pageState.currentSlideIndex,
      });
      this.updateArrows({
        ...pageState,
        currentPage: this.lockedPage,
      });
      return;
    }

    if (this.lockedPage !== null && performance.now() >= this.lockedUntil) {
      this.clearLock();
    }
    this.updatePagination(pageState);
    this.updateArrows(pageState);
  }

  updatePagination({ slides, slidesPerView, totalPages, currentPage }) {
    if (!this.pagination) return;

    if (totalPages <= 1) {
      this.pagination.hidden = true;
      this.pagination.innerHTML = '';
      this.paginationMeta = { totalPages: 0, slidesPerView: 0 };
      return;
    }

    this.pagination.hidden = false;

    if (
      this.pagination.childElementCount !== totalPages ||
      this.paginationMeta.totalPages !== totalPages ||
      this.paginationMeta.slidesPerView !== slidesPerView
    ) {
      this.pagination.innerHTML = '';
      for (let i = 0; i < totalPages; i += 1) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'collection-tabs-carousel__pagination-dot';
        button.setAttribute('aria-label', `Go to page ${i + 1}`);
        button.addEventListener('click', () => {
          this.goToPage(i, slidesPerView, slides.length);
        });
        this.pagination.appendChild(button);
      }
      this.paginationMeta = { totalPages, slidesPerView };
    }

    Array.from(this.pagination.children).forEach((dot, index) => {
      dot.setAttribute('aria-current', index === currentPage ? 'true' : 'false');
    });
  }

  updateArrows({ totalPages, currentPage, slidesPerView, totalSlides }) {
    const scroller = this.activeScroller;
    const canScrollPrev = scroller ? scroller.scrollLeft > 1 : currentPage > 0;
    const canScrollNext = scroller
      ? scroller.scrollLeft + scroller.clientWidth < scroller.scrollWidth - 1
      : currentPage < totalPages - 1;
    const canGoPrev = totalPages > 1 && canScrollPrev;
    const canGoNext = totalPages > 1 && canScrollNext;

    this.setArrowState(this.navPrev, canGoPrev);
    this.setArrowState(this.navNext, canGoNext);

    if (this.navPrev) {
      this.navPrev.dataset.targetPage = String(Math.max(0, currentPage - 1));
      this.navPrev.dataset.slidesPerView = String(slidesPerView);
      this.navPrev.dataset.totalSlides = String(totalSlides);
    }

    if (this.navNext) {
      this.navNext.dataset.targetPage = String(Math.min(totalPages - 1, currentPage + 1));
      this.navNext.dataset.slidesPerView = String(slidesPerView);
      this.navNext.dataset.totalSlides = String(totalSlides);
    }
  }

  getPageState() {
    if (!this.activeSlideshow) return null;
    const slideshow = this.activeSlideshow;
    const slides = this.getSlides();
    const slidesPerView = this.getSlidesPerView();
    const totalSlides = slides.length;
    const totalPages = Math.max(1, Math.ceil(totalSlides / slidesPerView));
    const currentSlideIndex = this.getCurrentSlideIndex(slides);
    const currentPage = Math.min(totalPages - 1, Math.max(0, Math.floor(currentSlideIndex / slidesPerView)));

    return {
      slides,
      slidesPerView,
      totalSlides,
      totalPages,
      currentSlideIndex,
      currentPage,
    };
  }

  getCurrentSlideIndex(slides) {
    const scroller = this.activeScroller;
    if (!scroller || !slides.length) {
      return typeof this.activeSlideshow?.current === 'number' ? this.activeSlideshow.current : 0;
    }

    const scrollLeft = scroller.scrollLeft;
    const baseOffset = slides[0]?.offsetLeft ?? 0;
    let closestIndex = 0;
    let closestDistance = Infinity;

    slides.forEach((slide, index) => {
      const slideLeft = slide.offsetLeft - baseOffset;
      const distance = Math.abs(slideLeft - scrollLeft);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    return closestIndex;
  }

  getSlidesPerView() {
    const scroller = this.activeScroller;
    const slides = this.getSlides();
    if (!scroller || !slides.length) return 1;

    const scrollerWidth = scroller.getBoundingClientRect().width;
    const slideWidth = this.getReferenceSlideWidth(slides);
    const styles = getComputedStyle(scroller);
    const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;

    if (!slideWidth) return 1;
    return Math.max(1, Math.floor((scrollerWidth + gap) / (slideWidth + gap)));
  }

  onResize() {
    if (this.resizeRafId !== null) return;
    this.resizeRafId = window.requestAnimationFrame(() => {
      this.resizeRafId = null;
      this.syncControls();
    });
  }

  onPrevClick(event) {
    if (!this.navPrev || this.isArrowDisabled(this.navPrev)) {
      event.preventDefault();
      return;
    }
    const pageState = this.getPageState();
    if (!pageState) return;
    this.goToPage(pageState.currentPage - 1, pageState.slidesPerView, pageState.totalSlides);
  }

  onNextClick(event) {
    if (!this.navNext || this.isArrowDisabled(this.navNext)) {
      event.preventDefault();
      return;
    }
    const pageState = this.getPageState();
    if (!pageState) return;
    this.goToPage(pageState.currentPage + 1, pageState.slidesPerView, pageState.totalSlides);
  }

  goToPage(pageIndex, slidesPerView, totalSlides) {
    if (!this.activeSlideshow) return;
    const pageState = this.getPageState();
    if (!pageState) return;
    const totalPages = Math.max(1, Math.ceil(totalSlides / slidesPerView));
    const safePage = Math.min(totalPages - 1, Math.max(0, pageIndex));
    const targetIndex = safePage * slidesPerView;
    this.lockedPage = safePage;
    this.lockedTargetIndex = targetIndex;
    this.lockedUntil = performance.now() + 800;
    this.renderControls({
      ...pageState,
      currentPage: safePage,
      currentSlideIndex: targetIndex,
    });
    this.selectIndex(targetIndex);
  }

  renderControls(state) {
    this.updatePagination(state);
    this.updateArrows(state);
  }

  scrollToIndex(index, behavior = 'smooth') {
    const scroller = this.activeScroller;
    const slides = this.getSlides();
    if (!scroller || !slides.length) return;

    const targetIndex = Math.min(Math.max(0, index), slides.length - 1);
    const targetSlide = slides[targetIndex];
    if (!targetSlide) return;

    const baseOffset = slides[0]?.offsetLeft ?? 0;
    const offset = targetSlide.offsetLeft - baseOffset;
    scroller.scrollTo({ left: offset, behavior });

    if (this.scrollingTimeout) {
      clearTimeout(this.scrollingTimeout);
    }
    this.scrollingTimeout = window.setTimeout(() => {
      this.scrollingTimeout = null;
      this.syncControls();
    }, 220);
  }

  selectIndex(index) {
    if (this.activeScroller) {
      this.scrollToIndex(index, 'smooth');
      return;
    }

    if (this.activeSlideshow && typeof this.activeSlideshow.select === 'function') {
      this.activeSlideshow.select(index);
    }
  }

  setArrowState(button, enabled) {
    if (!button) return;
    const isDisabled = !enabled;
    button.classList.toggle('is-disabled', isDisabled);
    button.setAttribute('aria-disabled', isDisabled ? 'true' : 'false');
    if (button.tagName === 'BUTTON') {
      button.disabled = isDisabled;
    }
  }

  isArrowDisabled(button) {
    return button.getAttribute('aria-disabled') === 'true' || button.classList.contains('is-disabled');
  }

  attachImageListeners(slideshow) {
    this.imageListeners = [];
    slideshow.querySelectorAll('img').forEach((image) => {
      if (image.complete) return;
      const handler = () => this.syncControls();
      image.addEventListener('load', handler, { once: true });
      image.addEventListener('error', handler, { once: true });
      this.imageListeners.push({ image, handler });
    });
  }

  cleanupActiveHandlers() {
    if (this.activeSlideshow && this.onSlideshowSelect) {
      this.activeSlideshow.removeEventListener('slideshow:select', this.onSlideshowSelect);
    }
    if (this.activeScroller && this.onScrollerScroll) {
      this.activeScroller.removeEventListener('scroll', this.onScrollerScroll);
    }
    if (this.scrollerResizeObserver) {
      this.scrollerResizeObserver.disconnect();
      this.scrollerResizeObserver = null;
    }
    if (this.scrollingTimeout) {
      clearTimeout(this.scrollingTimeout);
      this.scrollingTimeout = null;
    }
    this.imageListeners.forEach(({ image, handler }) => {
      image.removeEventListener('load', handler);
      image.removeEventListener('error', handler);
    });
    this.imageListeners = [];
    this.onSlideshowSelect = null;
    this.onScrollerScroll = null;
    this.activeSlideshow = null;
    this.activeScroller = null;
    this.activeSlides = [];
    this.clearLock();
  }

  getSlides() {
    if (!this.activeSlideshow) return [];
    const scroller = this.activeScroller || this.activeSlideshow.querySelector('slideshow-slides');
    if (!scroller) return [];
    const slides = Array.from(scroller.querySelectorAll(':scope > slideshow-slide[ref="slides[]"]')).filter((slide) => {
      const style = window.getComputedStyle(slide);
      return style.display !== 'none';
    });
    this.activeSlides = slides;
    return slides;
  }

  getReferenceSlideWidth(slides) {
    if (!slides.length) return 0;
    const productSlides = slides.filter((slide) => slide.querySelector('.collection-tabs-card'));
    const candidates = productSlides.length ? productSlides : slides;
    const widths = candidates
      .slice(0, 3)
      .map((slide) => slide.getBoundingClientRect().width)
      .filter((width) => width > 0);
    if (!widths.length) return 0;
    return widths.reduce((sum, width) => sum + width, 0) / widths.length;
  }

  clearLock() {
    this.lockedPage = null;
    this.lockedUntil = 0;
    this.lockedTargetIndex = null;
  }

  isAtTargetIndex(index) {
    if (index === null || index === undefined) return false;
    const scroller = this.activeScroller;
    const slides = this.getSlides();
    if (!scroller || !slides.length) return false;

    const targetIndex = Math.min(Math.max(0, index), slides.length - 1);
    const targetSlide = slides[targetIndex];
    if (!targetSlide) return false;

    const baseOffset = slides[0]?.offsetLeft ?? 0;
    const offset = targetSlide.offsetLeft - baseOffset;
    return Math.abs(scroller.scrollLeft - offset) <= 2;
  }

  /* ---------------------------------------------------------------- */
  /*  Elevar rec tracking: dl_view_item_list + dl_select_item          */
  /*  Mirrors assets/product-recommendations-constructor.js: observe   */
  /*  cards at 50% visibility (debounced batch), delegate product-link  */
  /*  clicks. Additive; wrapped so tracking never breaks the carousel.  */
  /* ---------------------------------------------------------------- */
  initElevarTracking() {
    this.elevarTrackedIds = new Set();
    this.elevarBatch = [];
    this.elevarBatchTimer = null;
    this.elevarObserver =
      'IntersectionObserver' in window
        ? new IntersectionObserver((entries) => this.onElevarIntersect(entries), { threshold: 0.5 })
        : null;
    // Capture phase so it records before the browser follows the link.
    this.section.addEventListener('click', (event) => this.onElevarSelectItem(event), true);
  }

  readElevarItem(card) {
    try {
      const script = card.querySelector('script.elevar-tc-item');
      if (!script) return null;
      return JSON.parse(script.textContent || 'null');
    } catch (err) {
      return null;
    }
  }

  observePanelCards(panel) {
    try {
      if (!window.BonobosElevar || !this.elevarObserver || !panel) return;
      const list = panel.dataset.productsUrl || window.location.pathname;
      const cards = Array.from(panel.querySelectorAll('.resource-list__item')).filter((card) =>
        card.querySelector('script.elevar-tc-item')
      );
      cards.forEach((card, index) => {
        card._elevarPosition = index + 1;
        card._elevarList = list;
        this.elevarObserver.observe(card);
      });
    } catch (err) {
      /* Tracking must never break the UI. */
    }
  }

  onElevarIntersect(entries) {
    let added = false;
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      this.elevarObserver.unobserve(entry.target);
      const item = this.readElevarItem(entry.target);
      if (!item) continue;
      const key = String(item.product_id || item.id || '');
      if (!key || this.elevarTrackedIds.has(key)) continue;
      this.elevarTrackedIds.add(key);
      this.elevarBatch.push({ item, position: entry.target._elevarPosition, list: entry.target._elevarList });
      added = true;
    }
    if (added) this.scheduleElevarFlush();
  }

  scheduleElevarFlush() {
    clearTimeout(this.elevarBatchTimer);
    this.elevarBatchTimer = setTimeout(() => this.flushElevarImpressions(), 500);
  }

  flushElevarImpressions() {
    const batch = this.elevarBatch;
    this.elevarBatch = [];
    clearTimeout(this.elevarBatchTimer);
    this.elevarBatchTimer = null;
    const el = window.BonobosElevar;
    if (!el || !batch.length) return;
    try {
      // Each impression carries its own `list` (its tab's collection URL) and is
      // enriched from its Shopify JSON before pushing dl_view_item_list.
      el.pushViewItemListEnriched(
        batch.map((entry) => ({
          product: entry.item,
          position: entry.position,
          list: entry.list,
        })),
      );
    } catch (err) {
      /* Tracking must never break the UI. */
    }
  }

  onElevarSelectItem(event) {
    try {
      const target = event.target;
      const link = target && target.closest ? target.closest('a[href*="/products/"]') : null;
      if (!link || !this.section.contains(link)) return;
      // Colour-swatch links preview a sibling product, not this card's item.
      if (link.closest('[data-product-card-swatch], .product-card__swatch, [ref="swatch"]')) return;
      this.pushElevarSelectItem(link.closest('.resource-list__item'));
    } catch (err) {
      /* Tracking must never break the UI. */
    }
  }

  // Fire dl_select_item for a clicked card. Called both from the product-link
  // listener above and from the quick-shop path (openQuickAddFromCarouselTrigger),
  // which stopImmediatePropagation()s before this listener would run — so the
  // quick-shop open must invoke this directly to be tracked.
  pushElevarSelectItem(card) {
    const el = window.BonobosElevar;
    if (!el || !card) return;
    try {
      const item = this.readElevarItem(card);
      if (!item) return;
      const list = card._elevarList || window.location.pathname;
      // Enrich from Shopify (cache warmed by the impression fetch); no await.
      el.pushSelectItemEnriched(item, card._elevarPosition, list);
    } catch (err) {
      /* Tracking must never break the UI. */
    }
  }

  getSavedIndex() {
    if (!this.storageKey) return null;
    try {
      const stored = window.localStorage.getItem(this.storageKey);
      const index = Number(stored);
      if (Number.isNaN(index)) return null;
      if (index < 0 || index >= this.tabs.length) return null;
      return index;
    } catch (error) {
      return null;
    }
  }

  saveIndex(index) {
    if (!this.storageKey) return;
    try {
      window.localStorage.setItem(this.storageKey, String(index));
    } catch (error) {
      return;
    }
  }
}

document.querySelectorAll('[data-collection-tabs-carousel]').forEach((section) => {
  if (section.dataset.collectionTabsCarouselInitialized === 'true') return;
  section.dataset.collectionTabsCarouselInitialized = 'true';
  new CollectionTabsCarousel(section);
});
